import React, { useState, useEffect } from 'react';
import { Modal } from '@components/common';
import { InvoiceItemsTable } from './InvoiceItemsTable';
import apiClient from '@/lib/api/client';
import type { Invoice, InvoiceItem, CreateInvoiceData, UpdateInvoiceData, InvoiceItemType, DiscountType } from '@lib/api/invoices';

interface Job {
  id: number;
  title: string;
}

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateInvoiceData | UpdateInvoiceData, invoiceId?: number) => Promise<void>;
  invoice?: Invoice & { items?: InvoiceItem[] };
  clientId: number;
  clientName: string;
  jobs?: Job[];
  /** Preselect this job when creating (e.g. opened from a job's Invoices tab). */
  defaultJobId?: number;
  /** Prefill line items when creating (e.g. from the billing tally, CR #174). */
  seedItems?: InvoiceItemInput[];
}

interface InvoiceItemInput {
  description: string;
  item_type?: InvoiceItemType;
  discount_type?: DiscountType;
  quantity: number;
  unit?: string;
  unit_price: number;
}

export const InvoiceModal: React.FC<InvoiceModalProps> = ({
  isOpen,
  onClose,
  onSave,
  invoice,
  clientId,
  clientName,
  jobs = [],
  defaultJobId,
  seedItems,
}) => {
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<number | undefined>(invoice?.job_id ?? defaultJobId);
  // #210 — pull job expenses in from inside the builder
  const [expOpen, setExpOpen] = useState(false);
  const [expList, setExpList] = useState<{ id: number; vendor: string | null; category: string; expense_date: string | null; total: number; description: string | null; invoiced_on_invoice_id: number | null }[]>([]);
  const [expLoading, setExpLoading] = useState(false);
  const [expSel, setExpSel] = useState<Set<number>>(new Set());
  const [expMarkup, setExpMarkup] = useState('20');
  const [expMode, setExpMode] = useState<'per' | 'summary'>('per');
  // expenses this invoice will claim on save (seeded ones come from the job tab)
  const [claimedExpenses, setClaimedExpenses] = useState<Record<string, number>>({});

  const openExpensePicker = async () => {
    if (!jobId) return;
    setExpOpen(true);
    setExpLoading(true);
    setExpSel(new Set());
    try {
      const r = await apiClient.get(`/jobs/${jobId}/expenses`);
      const rows = (r.data?.data?.expenses ?? []) as typeof expList;
      setExpList(rows);
    } catch { setExpList([]); } finally { setExpLoading(false); }
  };

  // Match the job tab's labels ("Materials", "Equipment Rental", …)
  const catLabel = (c: string) => String(c || 'other').replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase());

  const addSelectedExpenses = () => {
    const pct = Math.max(0, parseFloat(expMarkup) || 0);
    const mark = (n: number) => Math.round(n * (1 + pct / 100) * 100) / 100;
    const chosen = expList.filter(e => expSel.has(e.id) && !e.invoiced_on_invoice_id);
    if (chosen.length === 0) { setExpOpen(false); return; }
    const cost = chosen.reduce((a, e) => a + (Number(e.total) || 0), 0);
    const newItems = expMode === 'summary'
      ? [{ description: `Materials & expenses${pct ? ` (incl. ${pct}% markup)` : ''}`, item_type: 'item' as InvoiceItemType, quantity: 1, unit: '', unit_price: mark(cost) }]
      : chosen.map(e => ({
          description: `${catLabel(e.category)}${e.vendor ? ` — ${e.vendor}` : ''}${e.expense_date ? `, ${String(e.expense_date).slice(0, 10)}` : ''}${e.description ? ` (${e.description})` : ''}`,
          item_type: 'item' as InvoiceItemType, quantity: 1, unit: '', unit_price: mark(Number(e.total) || 0),
        }));
    setItems(prev => {
      const blankLast = prev.length === 1 && !prev[0].description && !prev[0].unit_price;
      return blankLast ? newItems : [...prev, ...newItems];
    });
    setClaimedExpenses(prev => {
      const next = { ...prev };
      for (const e of chosen) next[String(e.id)] = mark(Number(e.total) || 0);
      return next;
    });
    setExpOpen(false);
  };
  const [invoiceDate, setInvoiceDate] = useState(
    invoice?.invoice_date || new Date().toISOString().split('T')[0]
  );
  const [dueDate, setDueDate] = useState(
    invoice?.due_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [taxRate, setTaxRate] = useState(invoice?.tax_rate || 0);
  const [bcCompliance, setBcCompliance] = useState(!!(invoice as any)?.gst_rate);
  const [gstRate, setGstRate] = useState((invoice as any)?.gst_rate ?? 5);
  const [pstRate, setPstRate] = useState((invoice as any)?.pst_rate ?? 7);
  const [holdbackPercent, setHoldbackPercent] = useState((invoice as any)?.holdback_percent ? Number((invoice as any).holdback_percent) * 100 : 10);
  const [notes, setNotes] = useState(invoice?.notes || '');
  const [items, setItems] = useState<InvoiceItemInput[]>(
    invoice?.items?.map((item) => ({
      description: item.description,
      item_type: item.item_type || 'item',
      discount_type: item.discount_type,
      quantity: item.quantity,
      unit: item.unit,
      unit_price: item.unit_price,
    })) || (seedItems?.length ? seedItems : [{ description: '', item_type: 'item', quantity: 1, unit: '', unit_price: 0 }])
  );
  const [revisionNotes, setRevisionNotes] = useState('');

  // Detect non-draft edit (will create a revision)
  const isRevision = !!invoice && invoice.status !== 'draft';

  useEffect(() => {
    if (invoice) {
      setJobId(invoice.job_id);
      setInvoiceDate(invoice.invoice_date?.split('T')[0] || new Date().toISOString().split('T')[0]);
      setDueDate(invoice.due_date?.split('T')[0] || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
      setTaxRate(invoice.tax_rate || 0);
      setBcCompliance(!!(invoice as any)?.gst_rate);
      setGstRate((invoice as any)?.gst_rate ?? 5);
      setPstRate((invoice as any)?.pst_rate ?? 7);
      setHoldbackPercent((invoice as any)?.holdback_percent ? Number((invoice as any).holdback_percent) * 100 : 10);
      setNotes(invoice.notes || '');
      setItems(
        invoice.items?.map((item) => ({
          description: item.description,
          item_type: item.item_type || 'item',
          discount_type: item.discount_type,
          quantity: item.quantity,
          unit: item.unit,
          unit_price: item.unit_price,
        })) || [{ description: '', item_type: 'item', quantity: 1, unit: '', unit_price: 0 }]
      );
    } else {
      // CR #172: opened from a job's Invoices tab → that job is preselected;
      // standalone opens (no context) still start blank.
      setJobId(defaultJobId);
      setInvoiceDate(new Date().toISOString().split('T')[0]);
      setDueDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
      setTaxRate(0);
      setBcCompliance(false);
      setGstRate(5);
      setPstRate(7);
      setHoldbackPercent(10);
      setNotes('');
      setItems(seedItems?.length ? seedItems : [{ description: '', item_type: 'item', quantity: 1, unit: '', unit_price: 0 }]);
    }
    setRevisionNotes('');
  }, [invoice, isOpen, defaultJobId]);

  // Calculate subtotal of regular items only
  const calculateItemsSubtotal = () => {
    return items
      .filter((item) => item.item_type !== 'discount')
      .reduce((sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unit_price) || 0), 0);
  };

  // Calculate total discount amount
  const calculateDiscounts = () => {
    const itemsSubtotal = calculateItemsSubtotal();
    return items
      .filter((item) => item.item_type === 'discount')
      .reduce((sum, item) => {
        if (item.discount_type === 'percent') {
          return sum + (itemsSubtotal * (Number(item.unit_price) || 0)) / 100;
        } else {
          return sum + (Number(item.quantity) || 1) * (Number(item.unit_price) || 0);
        }
      }, 0);
  };

  // Subtotal after discounts
  const calculateSubtotal = () => {
    return calculateItemsSubtotal() - calculateDiscounts();
  };

  const calculateGst = () => bcCompliance ? calculateSubtotal() * (Number(gstRate) / 100) : 0;
  const calculatePst = () => bcCompliance ? calculateSubtotal() * (Number(pstRate) / 100) : 0;
  const calculateHoldback = () => bcCompliance ? calculateSubtotal() * (Number(holdbackPercent) / 100) : 0;

  const calculateTax = () => {
    if (bcCompliance) return calculateGst() + calculatePst();
    return calculateSubtotal() * (Number(taxRate) / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Filter out empty items
    const validItems = items.filter((item) => item.description.trim() !== '');

    if (validItems.length === 0) {
      setFormError('Please add at least one line item');
      return;
    }

    setLoading(true);
    try {
      const bcFields = bcCompliance ? {
        gst_rate: Number(gstRate) / 100,
        pst_rate: Number(pstRate) / 100,
        gst_amount: calculateGst(),
        pst_amount: calculatePst(),
        holdback_percent: Number(holdbackPercent) / 100,
        holdback_amount: calculateHoldback(),
      } : {};

      if (invoice) {
        const updateData: UpdateInvoiceData = {
          invoice_date: invoiceDate,
          due_date: dueDate,
          tax_rate: bcCompliance ? (Number(gstRate) + Number(pstRate)) : taxRate,
          notes: notes || undefined,
          items: validItems,
          ...(isRevision && revisionNotes ? { revision_notes: revisionNotes } : {}),
          ...bcFields,
        } as UpdateInvoiceData;
        await onSave(updateData, invoice.id);
      } else {
        const createData: CreateInvoiceData = {
          client_id: clientId,
          job_id: jobId,
          invoice_date: invoiceDate,
          due_date: dueDate,
          tax_rate: bcCompliance ? (Number(gstRate) + Number(pstRate)) : taxRate,
          notes: notes || undefined,
          items: validItems,
          ...bcFields,
          ...(Object.keys(claimedExpenses).length
            ? { expense_ids: Object.keys(claimedExpenses).map(Number), expense_billed: claimedExpenses }
            : {}),
        } as CreateInvoiceData;
        await onSave(createData);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save invoice:', error);
      setFormError('Failed to save invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={invoice ? (isRevision ? `Create Revision of ${invoice.invoice_number}` : `Edit Invoice ${invoice.invoice_number}`) : 'Create Invoice'}
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors border border-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="invoice-form"
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : invoice ? (isRevision ? 'Create Revision' : 'Update Invoice') : 'Create Invoice'}
          </button>
        </div>
      }
    >
      <form id="invoice-form" onSubmit={handleSubmit} className="space-y-6 bg-white">
        {/* Client Info */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-500 mb-1">Client</div>
          <div className="text-gray-900 font-medium">{clientName}</div>
        </div>

        {/* Invoice Details Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Job Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job (Optional)
            </label>
            <select
              value={jobId || ''}
              onChange={(e) => setJobId(e.target.value ? Number(e.target.value) : undefined)}
              className="w-full px-4 py-2 rounded-lg bg-white text-gray-900 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
            >
              <option value="">No job selected</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.title}
                </option>
              ))}
            </select>
          </div>

          {/* Tax / BC Compliance */}
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Tax</label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={bcCompliance}
                  onChange={e => setBcCompliance(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                BC Compliance (GST + PST + Holdback)
              </label>
            </div>
            {bcCompliance ? (
              <div className="grid grid-cols-3 gap-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div>
                  <label className="block text-xs font-medium text-blue-700 mb-1">GST Rate (%)</label>
                  <input type="number" value={gstRate} onChange={e => setGstRate(parseFloat(e.target.value) || 0)}
                    step="0.01" min="0" max="100"
                    className="w-full px-3 py-1.5 rounded bg-white text-gray-900 border border-blue-300 text-sm focus:border-primary focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-blue-700 mb-1">PST Rate (%)</label>
                  <input type="number" value={pstRate} onChange={e => setPstRate(parseFloat(e.target.value) || 0)}
                    step="0.01" min="0" max="100"
                    className="w-full px-3 py-1.5 rounded bg-white text-gray-900 border border-blue-300 text-sm focus:border-primary focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-blue-700 mb-1">Holdback (%)</label>
                  <input type="number" value={holdbackPercent} onChange={e => setHoldbackPercent(parseFloat(e.target.value) || 0)}
                    step="1" min="0" max="100"
                    className="w-full px-3 py-1.5 rounded bg-white text-gray-900 border border-blue-300 text-sm focus:border-primary focus:outline-none" />
                </div>
              </div>
            ) : (
              <input
                type="number"
                value={taxRate}
                onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                min="0" max="100" step="0.01"
                className="w-full px-4 py-2 rounded-lg bg-white text-gray-900 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                placeholder="Tax rate (%)"
              />
            )}
          </div>

          {/* Invoice Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Date
            </label>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              required
              className="w-full px-4 py-2 rounded-lg bg-white text-gray-900 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
            />
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Due Date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
              className="w-full px-4 py-2 rounded-lg bg-white text-gray-900 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
            />
          </div>
        </div>

        {/* Line Items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Line Items
            </label>
            {/* #210 — bill this job's receipts straight from the builder */}
            {!invoice && (
              <button
                type="button"
                onClick={openExpensePicker}
                disabled={!jobId}
                title={jobId ? "Add this job's expenses as billable lines" : 'Pick a job first'}
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:border-primary hover:text-primary disabled:opacity-40"
              >
                <span className="material-symbols-outlined text-base">receipt_long</span>
                Add expenses…
              </button>
            )}
          </div>
          <InvoiceItemsTable items={items} onItemsChange={setItems} />
          {Object.keys(claimedExpenses).length > 0 && (
            <p className="mt-1.5 text-xs text-green-700">
              {Object.keys(claimedExpenses).length} job expense{Object.keys(claimedExpenses).length === 1 ? '' : 's'} will be marked billed when you save this invoice.
            </p>
          )}
        </div>

        {/* #210 — expense picker */}
        {expOpen && (
          <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setExpOpen(false)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={ev => ev.stopPropagation()}>
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="font-bold text-gray-900">Add job expenses</h3>
                <p className="text-xs text-gray-500 mt-0.5">Only expenses not already billed can be added.</p>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
                {expLoading ? (
                  <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
                ) : expList.length === 0 ? (
                  <p className="text-sm text-gray-400 py-6 text-center">No expenses logged on this job.</p>
                ) : expList.map(e => {
                  const billed = !!e.invoiced_on_invoice_id;
                  return (
                    <label key={e.id} className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 ${billed ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-gray-200 hover:border-primary/40 cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        disabled={billed}
                        checked={expSel.has(e.id)}
                        onChange={() => setExpSel(prev => { const n = new Set(prev); if (n.has(e.id)) n.delete(e.id); else n.add(e.id); return n; })}
                        className="w-4 h-4 accent-[#C77D3F]"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-gray-900 truncate">{e.vendor || 'Unknown vendor'}</span>
                        <span className="block text-xs text-gray-500 truncate">
                          {catLabel(e.category)}{e.expense_date ? ` · ${String(e.expense_date).slice(0, 10)}` : ''}{billed ? ' · already billed' : ''}
                        </span>
                      </span>
                      <span className="text-sm font-bold text-gray-900">${Number(e.total).toFixed(2)}</span>
                    </label>
                  );
                })}
              </div>
              <div className="border-t border-gray-100 p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">Markup</span>
                  <input inputMode="decimal" value={expMarkup} onChange={ev => setExpMarkup(ev.target.value)}
                    className="h-9 w-20 px-2 rounded-lg border border-gray-300 text-sm" />
                  <span className="text-sm text-gray-500">%</span>
                  <div className="ml-auto flex gap-1">
                    {([['per', 'One line each'], ['summary', 'Summary line']] as const).map(([m, label]) => (
                      <button key={m} type="button" onClick={() => setExpMode(m)}
                        className={`h-8 px-2.5 rounded-lg text-xs font-semibold border ${expMode === m ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setExpOpen(false)} className="h-10 px-4 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
                  <button type="button" onClick={addSelectedExpenses} disabled={expSel.size === 0}
                    className="h-10 px-4 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-40">
                    Add {expSel.size > 0 ? expSel.size : ''} to invoice
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Additional notes or payment instructions..."
            className="w-full px-4 py-2 rounded-lg bg-white text-gray-900 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none resize-none"
          />
        </div>

        {/* Revision Notes (only shown when creating a revision) */}
        {isRevision && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Revision Notes
            </label>
            <textarea
              value={revisionNotes}
              onChange={(e) => setRevisionNotes(e.target.value)}
              rows={2}
              placeholder="Describe what changed in this revision..."
              className="w-full px-4 py-2 rounded-lg bg-white text-gray-900 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              This will create a new revision. The original invoice will be marked as &quot;Revised&quot;.
            </p>
          </div>
        )}

        {/* Totals */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-2">
          <div className="flex justify-between text-gray-600">
            <span>Items Subtotal</span>
            <span>{formatCurrency(calculateItemsSubtotal())}</span>
          </div>
          {calculateDiscounts() > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Discounts</span>
              <span>-{formatCurrency(calculateDiscounts())}</span>
            </div>
          )}
          <div className="flex justify-between text-gray-600">
            <span>Subtotal</span>
            <span>{formatCurrency(calculateSubtotal())}</span>
          </div>
          {bcCompliance ? (
            <>
              <div className="flex justify-between text-gray-600">
                <span>GST ({gstRate}%)</span>
                <span>{formatCurrency(calculateGst())}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>PST ({pstRate}%)</span>
                <span>{formatCurrency(calculatePst())}</span>
              </div>
              <div className="flex justify-between text-orange-700 border-t border-dashed border-gray-300 pt-2 mt-1">
                <span className="text-sm">10% Holdback (BC Liens Act)</span>
                <span>-{formatCurrency(calculateHoldback())}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between text-gray-600">
              <span>Tax ({taxRate}%)</span>
              <span>{formatCurrency(calculateTax())}</span>
            </div>
          )}
          <div className="flex justify-between text-gray-900 font-semibold text-lg border-t border-gray-300 pt-2 mt-2">
            <span>{bcCompliance ? 'Total (after holdback)' : 'Total'}</span>
            <span>{formatCurrency(bcCompliance ? calculateTotal() - calculateHoldback() : calculateTotal())}</span>
          </div>
          {bcCompliance && (
            <p className="text-xs text-orange-600 mt-1">
              Holdback of {formatCurrency(calculateHoldback())} released 55 days after substantial completion (BC Builders Lien Act)
            </p>
          )}
        </div>

        {formError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-2 text-red-700 text-sm">
            <span className="material-symbols-outlined text-base mt-0.5 flex-shrink-0">error</span>
            <span>{formError}</span>
          </div>
        )}
      </form>
    </Modal>
  );
};
